<template>
  <div class="retention-messages">
    <retention-tabs />

    <div class="page-header">
      <div>
        <h2>Retention Messages</h2>
        <p class="hint">
          Compose retention messages. Edit while in draft, then submit for Apple's review.
          Approved messages are immutable — to change content, create a new draft and delete the old one.
        </p>
      </div>
      <retention-message-create v-if="canCreateRetentionMessage && isConfigured" />
      <span v-else-if="!canCreateRetentionMessage" class="permission-note">No permission to create retention messages</span>
    </div>

    <div v-if="isLoading" class="empty">Loading…</div>
    <div v-else-if="sortedMessages.length === 0" class="empty">
      No retention messages yet.
      <span v-if="canCreateRetentionMessage">Click "+ Create Message" to create your first draft.</span>
    </div>

    <template v-else>
      <div class="filter-toolbar">
        <div class="search-input">
          <i class="fas fa-search"></i>
          <input
            v-model="messageSearch"
            type="text"
            placeholder="Search by name…"
          />
          <i v-if="messageSearch" class="fas fa-times clear-icon" @click="messageSearch = ''" />
        </div>
        <label class="filter-label">
          Language
          <select v-model="languageFilter" class="filter-select">
            <option value="">All languages</option>
            <option value="__none__">No language label</option>
            <option v-for="loc in supportedLocales" :key="loc.code" :value="loc.code">
              {{ loc.label }} — {{ loc.code }}
            </option>
          </select>
        </label>
        <label class="filter-label">
          Type
          <select v-model="typeFilter" class="filter-select">
            <option value="">All types</option>
            <option v-for="t in typeOptions" :key="t.value" :value="t.value">
              {{ t.label }}
            </option>
          </select>
        </label>
        <button
          v-if="messageSearch || languageFilter || typeFilter"
          class="filter-clear"
          type="button"
          @click="clearFilters"
        >
          Clear
        </button>
        <span class="filter-count">{{ visibleMessages.length }} / {{ sortedMessages.length }}</span>
      </div>

      <div class="table-wrap">
        <table class="messages-table">
          <thead>
            <tr>
              <th class="col-check" @click.stop>
                <input
                  type="checkbox"
                  :checked="allVisibleSelected"
                  :indeterminate.prop="someVisibleSelected"
                  @change="toggleAllVisible($event)"
                />
              </th>
              <th class="col-status">Status</th>
              <th class="col-name">Name</th>
              <th class="col-language">Language</th>
              <th class="col-id">ID</th>
              <th class="col-type">Type</th>
              <th class="col-title">Title</th>
              <th class="col-body">Body</th>
              <th class="col-req">Req #</th>
              <th class="col-actions"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="groupedRows.length === 0">
              <td colspan="10" class="empty-row">No messages match these filters.</td>
            </tr>
            <template v-for="row in groupedRows">
              <!-- Group header row -->
              <tr
                v-if="row.kind === 'group'"
                :key="`grp-${row.groupId}`"
                class="group-header-row"
                @click="toggleGroup(row.groupId)"
              >
                <td class="col-check" @click.stop>
                  <input
                    type="checkbox"
                    :checked="groupFullySelected(row)"
                    :indeterminate.prop="groupPartiallySelected(row)"
                    @change="selectAllInGroup(row, $event)"
                  />
                </td>
                <td class="group-label-cell" colspan="7">
                  <i class="group-caret fas" :class="row.expanded ? 'fa-chevron-down' : 'fa-chevron-right'"></i>
                  <i class="fas fa-layer-group group-icon"></i>
                  <span class="group-name">{{ row.name }}</span>
                  <span
                    class="group-badge"
                    :title="row.visibleMembers.length < row.childCount ? `${row.visibleMembers.length} of ${row.childCount} shown` : ''"
                  >
                    {{ row.childCount }} {{ row.childCount === 1 ? 'language' : 'languages' }}
                  </span>
                </td>
                <td
                  class="req-cell group-req-total"
                  :title="`Total requests across ${row.childCount} message${row.childCount === 1 ? '' : 's'}`"
                >
                  {{ row.reqTotal.toLocaleString() }}
                </td>
                <td class="col-actions" @click.stop>
                  <popup-menu v-if="canDeleteRetentionMessage">
                    <template v-slot:trigger>
                      <i class="fas fa-ellipsis-v" />
                    </template>
                    <template v-slot:content>
                      <div>
                        <a @click="deleteGroupConfirm(row)" class="danger">
                          <i class="fas fa-trash" /> Delete entire group
                        </a>
                      </div>
                    </template>
                  </popup-menu>
                </td>
              </tr>
              <!-- Standalone or group-member message row -->
              <tr
                v-else
                :key="`msg-${row.message.id}`"
                class="data-row"
                :class="{ selected: selectedIds.has(row.message.id), 'group-child': row.kind === 'member' }"
                @click="viewMessage(row.message.id)"
              >
                <td class="col-check" @click.stop>
                  <input
                    type="checkbox"
                    :checked="selectedIds.has(row.message.id)"
                    @change="toggleItem(row.message.id, $event)"
                  />
                </td>
                <td>
                  <span class="state-pill" :class="`state-${(row.message.messageState || 'draft').toLowerCase()}`">
                    {{ row.message.messageState || 'draft' }}
                  </span>
                </td>
                <td class="name-cell">{{ row.message.name || '(unnamed)' }}</td>
                <td class="language-cell">
                  <span v-if="row.message.language" class="language-chip">{{ getLocaleLabel(row.message.language) }}</span>
                  <span v-else class="muted">—</span>
                </td>
                <td>
                  <code class="id-cell" @click.stop="copyMessageId(row.message.id)" :title="`Click to copy: ${row.message.id}`">
                    {{ row.message.id.slice(0, 8) }}…
                  </code>
                </td>
                <td class="type-cell">
                  <span class="type-chip" :class="`type-chip-${typeColorOf(row.message.messageType)}`">
                    <i :class="typeIconOf(row.message.messageType)"></i>
                    {{ typeLabelOf(row.message.messageType) }}
                  </span>
                </td>
                <td class="title-cell">{{ row.message.header || '(no title)' }}</td>
                <td class="body-cell">{{ truncate(row.message.body, 90) }}</td>
                <td class="req-cell">
                  {{ ((row.message.statistics && row.message.statistics.requestCount) || 0).toLocaleString() }}
                </td>
                <td class="col-actions" @click.stop>
                  <popup-menu>
                    <template v-slot:trigger>
                      <i class="fas fa-ellipsis-v" />
                    </template>
                    <template v-slot:content>
                      <div>
                        <a @click="viewMessage(row.message.id)">
                          <i :class="row.message.messageState === 'draft' ? 'fas fa-pen-square' : 'fas fa-eye'" />
                          {{ canEditFor(row.message) ? 'Edit' : 'View' }}
                        </a>
                      </div>
                      <div v-if="canCreateRetentionMessage">
                        <a @click="openDuplicateModal(row.message)">
                          <i class="fas fa-globe" /> Duplicate to other languages
                        </a>
                      </div>
                      <div v-if="canAddToGroup(row.message)">
                        <a @click="openAddToGroup(row.message)">
                          <i class="fas fa-layer-group" /> Add to group…
                        </a>
                      </div>
                      <div v-if="canSubmitFor(row.message)">
                        <a @click="submitMessage(row.message.id)">
                          <i class="fas fa-paper-plane" /> Submit for review
                        </a>
                      </div>
                      <div v-if="canDeleteRetentionMessage">
                        <a @click="deleteMessage(row.message.id)" class="danger">
                          <i class="fas fa-trash" /> Delete
                        </a>
                      </div>
                    </template>
                  </popup-menu>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>
    </template>

    <!-- Sticky bulk-action bar -->
    <div
      v-if="selectedList.length > 0 && !bulkRunning && (canSubmitRetentionMessage || canDeleteRetentionMessage)"
      class="bulk-bar"
    >
      <span class="bulk-count">
        <strong>{{ selectedList.length }}</strong>
        {{ selectedList.length === 1 ? 'message' : 'messages' }} selected
      </span>
      <button
        v-if="canSubmitRetentionMessage"
        class="btn btn-primary"
        :disabled="submittableSelected.length === 0"
        @click="bulkSubmit"
      >
        Submit {{ submittableSelected.length }} {{ submittableSelected.length === 1 ? 'draft' : 'drafts' }}
      </button>
      <button class="link-btn" @click="selectNone">Clear selection</button>
      <div class="bulk-bar-right">
        <button
          v-if="canDeleteRetentionMessage"
          class="btn btn-remove-default"
          @click="bulkDelete"
        >
          Delete {{ deletableSelected.length }}
        </button>
      </div>
      <p
        v-if="canSubmitRetentionMessage && submittableSelected.length < selectedList.length"
        class="bulk-hint"
      >
        {{ selectedList.length - submittableSelected.length }} selected item(s) aren't drafts and will be skipped on submit.
      </p>
    </div>

    <retention-bulk-runner-modals
      unit="message"
      :running="bulkRunning"
      :progress="bulkProgress"
      :show-result="showResultModal"
      :result="bulkResult"
      :running-verb="bulkRunningVerb"
      :complete-title="bulkCompleteTitle"
      :retryable-failures="retryableFailures"
      :terminal-failures="terminalFailures"
      @retry="retryFailed"
      @close="closeResultModal"
    />

    <retention-duplicate-languages-modal
      v-if="duplicateBaseMessage"
      :base-message="duplicateBaseMessage"
      @close="duplicateBaseMessage = null"
    />

    <retention-add-to-group-modal
      v-if="addToGroupSource"
      :source-message="addToGroupSource"
      @close="addToGroupSource = null"
    />

    <!-- Blocking overlay while a whole group is being deleted -->
    <div v-if="deletingGroupName" class="group-delete-overlay">
      <div class="group-delete-card">
        <div class="spinner-border text-info" role="status"></div>
        <div>
          <h3>Deleting group "{{ deletingGroupName }}"…</h3>
          <p>Removing its messages from Apple; this can take a few seconds.</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import RetentionMessageCreate from '../../components/Retention/MessageCreate.vue';
import RetentionDuplicateLanguagesModal from '../../components/Retention/DuplicateLanguagesModal.vue';
import RetentionAddToGroupModal from '../../components/Retention/AddToGroupModal.vue';
import RetentionBulkRunnerModals from '../../components/Retention/RetentionBulkRunnerModals.vue';
import PopupMenu from '../PushNotification/UIComponents/PopupMenu.vue';
import RetentionTabs from './Tabs.vue';
import { permissionsMixin } from '../../mixins/permissions.mixin';
import { retentionBulkRunnerMixin } from '../../mixins/retentionBulkRunner.mixin';
import { SUPPORTED_LOCALES } from '../../constants/retentionLocales';
import { MESSAGE_TYPE_META, META_BY_TYPE } from '../../constants/retentionMessageTypes';
import notify from '../../util/notification';

const LOCALE_LABEL_BY_CODE = SUPPORTED_LOCALES.reduce((acc, l) => {
  acc[l.code] = l.label;
  return acc;
}, {});

export default {
  mixins: [permissionsMixin, retentionBulkRunnerMixin],
  components: {
    RetentionMessageCreate,
    RetentionDuplicateLanguagesModal,
    RetentionAddToGroupModal,
    RetentionBulkRunnerModals,
    PopupMenu,
    RetentionTabs
  },
  data() {
    return {
      messageSearch: '',
      languageFilter: '',
      typeFilter: '',
      duplicateBaseMessage: null,
      addToGroupSource: null,
      deletingGroupName: null,
      expandedGroups: new Set(),
      supportedLocales: SUPPORTED_LOCALES,
      typeOptions: MESSAGE_TYPE_META.map((m) => ({ value: m.type, label: m.label })),
      selectedIds: new Set(),
      lastClickedIndex: null,
      bulkAction: null
    };
  },
  computed: {
    ...mapGetters('retention', ['isConfiguredFor']),
    isConfigured() {
      return this.isConfiguredFor(this.$store.getters.activeApp);
    },
    retentionMessages() {
      return this.$store.getters.retentionMessages || [];
    },
    isLoading() {
      return this.$store.getters['retention/isLoadingMessages'];
    },
    sortedMessages() {
      return [...this.retentionMessages].sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
    },
    visibleMessages() {
      const search = this.messageSearch.trim().toLowerCase();
      const filter = this.languageFilter;
      const typeFilter = this.typeFilter;
      return this.sortedMessages.filter((m) => {
        if (search && !(m.name || '').toLowerCase().includes(search)) return false;
        if (filter === '__none__' && m.language) return false;
        if (filter && filter !== '__none__' && m.language !== filter) return false;
        if (typeFilter && (m.messageType || 'text') !== typeFilter) return false;
        return true;
      });
    },
    groupsById() {
      return this.$store.getters['retention/groupsById'] || {};
    },
    // Grouped display model, built from the FULL list so filtered-out members still
    // resolve their group; a group with no visible members emits no header.
    groupedRows() {
      const visible = this.visibleMessages;
      const visibleIds = new Set(visible.map((m) => m.id));
      const membersByGroup = {};
      for (const m of this.sortedMessages) {
        if (!m.groupId) continue;
        (membersByGroup[m.groupId] = membersByGroup[m.groupId] || []).push(m);
      }
      const rows = [];
      const emitted = new Set();
      for (const m of visible) {
        const gid = m.groupId;
        const members = gid ? (membersByGroup[gid] || [m]) : null;
        // Standalone, or a degenerate 1-member group (missed dissolve) renders as a plain row.
        if (!gid || members.length < 2) {
          rows.push({ kind: 'standalone', message: m });
          continue;
        }
        if (emitted.has(gid)) continue;
        emitted.add(gid);
        const visibleMembers = members.filter((x) => visibleIds.has(x.id));
        const group = this.groupsById[gid];
        rows.push({
          kind: 'group',
          groupId: gid,
          name: group ? group.name : '(unnamed group)',
          members,
          visibleMembers,
          childCount: members.length,
          reqTotal: members.reduce((sum, x) => sum + ((x.statistics && x.statistics.requestCount) || 0), 0),
          expanded: this.expandedGroups.has(gid)
        });
        if (this.expandedGroups.has(gid)) {
          for (const c of visibleMembers) rows.push({ kind: 'member', message: c, groupId: gid });
        }
      }
      return rows;
    },
    // Rendered selectable ids in DOM order - the index space for shift-select/select-all.
    renderedMessageIds() {
      const ids = [];
      for (const row of this.groupedRows) {
        if (row.kind === 'standalone' || row.kind === 'member') ids.push(row.message.id);
      }
      return ids;
    },
    // Selection persists across filter changes, so resolve against the full list.
    selectedList() {
      return this.retentionMessages.filter((m) => this.selectedIds.has(m.id));
    },
    submittableSelected() {
      return this.selectedList.filter((m) => (m.messageState || 'draft') === 'draft');
    },
    deletableSelected() {
      return this.selectedList;
    },
    allVisibleSelected() {
      return (
        this.renderedMessageIds.length > 0 &&
        this.renderedMessageIds.every((id) => this.selectedIds.has(id))
      );
    },
    someVisibleSelected() {
      const some = this.renderedMessageIds.some((id) => this.selectedIds.has(id));
      return some && !this.allVisibleSelected;
    },
    bulkRunningVerb() {
      return this.bulkAction === 'submit' ? 'Submitting' : 'Deleting';
    },
    bulkCompleteTitle() {
      return this.bulkAction === 'submit' ? 'Bulk submit' : 'Bulk delete';
    }
  },
  watch: {
    // Any reindex (filter/expand/collapse) invalidates the shift-click anchor.
    renderedMessageIds() {
      this.lastClickedIndex = null;
    }
  },
  async mounted() {
    await Promise.all([
      this.$store.dispatch('retention/fetchMessages'),
      this.$store.dispatch('retention/fetchImages'),
      this.$store.dispatch('retention/fetchGroups')
    ]);
  },
  beforeRouteLeave(to, from, next) {
    if (this.bulkRunning) {
      if (!confirm('A bulk operation is still running. Leave anyway?')) return next(false);
    }
    next();
  },
  methods: {
    viewMessage(id) {
      this.$router.push({
        name: 'RetentionMessage',
        params: { aid: this.$store.getters.activeApp, id }
      });
    },
    canEditFor(message) {
      // Drafts: full edit. APPROVED: name-only edit (handled inside the detail page).
      if (!this.canEditRetentionMessage) return false;
      return message.messageState === 'draft' || message.messageState === 'APPROVED';
    },
    canSubmitFor(message) {
      return message.messageState === 'draft' && this.canSubmitRetentionMessage;
    },
    async submitMessage(id) {
      if (!confirm('Submit this draft to Apple for review? It will become immutable.')) return;
      await this.$store.dispatch('retention/submitMessage', id);
    },
    async deleteMessage(id) {
      const message = this.retentionMessages.find(m => m.id === id);
      const label = message?.name || message?.header || id;
      let prompt = `Delete "${label}"?`;
      if (message?.groupId) {
        const membersByGroup = this.$store.getters['retention/messagesByGroupId'] || {};
        const count = (membersByGroup[message.groupId] || []).length;
        if (count <= 2) {
          prompt = `Delete "${label}"? It is one of ${count} messages in its group; deleting it dissolves the group and the remaining message becomes standalone.`;
        }
      }
      if (!confirm(prompt)) return;
      await this.$store.dispatch('retention/deleteMessage', id);
    },
    copyMessageId(id) {
      const tempInput = document.createElement('input');
      tempInput.value = id;
      document.body.appendChild(tempInput);
      tempInput.select();
      document.execCommand('copy');
      document.body.removeChild(tempInput);
    },
    truncate(text, n) {
      if (!text) return '';
      return text.length > n ? `${text.slice(0, n)}…` : text;
    },
    getLocaleLabel(code) {
      return LOCALE_LABEL_BY_CODE[code] || code;
    },
    clearFilters() {
      this.messageSearch = '';
      this.languageFilter = '';
      this.typeFilter = '';
    },
    typeLabelOf(type) {
      const meta = META_BY_TYPE[type] || META_BY_TYPE.text;
      return meta.short;
    },
    typeIconOf(type) {
      const meta = META_BY_TYPE[type] || META_BY_TYPE.text;
      return meta.icon;
    },
    typeColorOf(type) {
      const meta = META_BY_TYPE[type] || META_BY_TYPE.text;
      return meta.chipColor;
    },
    openDuplicateModal(message) {
      this.duplicateBaseMessage = message;
    },
    toggleItem(id, event) {
      const idx = this.renderedMessageIds.indexOf(id);
      if (event && event.shiftKey && this.lastClickedIndex !== null && idx !== -1) {
        const [from, to] = [this.lastClickedIndex, idx].sort((a, b) => a - b);
        const target = !this.selectedIds.has(id);
        const next = new Set(this.selectedIds);
        for (let i = from; i <= to; i++) {
          const c = this.renderedMessageIds[i];
          if (!c) continue;
          if (target) next.add(c);
          else next.delete(c);
        }
        this.selectedIds = next;
      } else {
        const next = new Set(this.selectedIds);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        this.selectedIds = next;
      }
      this.lastClickedIndex = idx;
    },
    toggleAllVisible(event) {
      const target = event.target.checked;
      const next = new Set(this.selectedIds);
      for (const id of this.renderedMessageIds) {
        if (target) next.add(id);
        else next.delete(id);
      }
      this.selectedIds = next;
    },
    toggleGroup(groupId) {
      const next = new Set(this.expandedGroups);
      if (next.has(groupId)) next.delete(groupId);
      else next.add(groupId);
      this.expandedGroups = next;
    },
    groupFullySelected(row) {
      return (
        row.visibleMembers.length > 0 &&
        row.visibleMembers.every((m) => this.selectedIds.has(m.id))
      );
    },
    groupPartiallySelected(row) {
      const some = row.visibleMembers.some((m) => this.selectedIds.has(m.id));
      return some && !this.groupFullySelected(row);
    },
    selectAllInGroup(row, event) {
      const target = event ? event.target.checked : true;
      const next = new Set(this.selectedIds);
      for (const m of row.visibleMembers) {
        if (target) next.add(m.id);
        else next.delete(m.id);
      }
      this.selectedIds = next;
    },
    canAddToGroup(message) {
      return this.canCreateRetentionMessage && !message.groupId && !!message.language;
    },
    openAddToGroup(message) {
      this.addToGroupSource = message;
    },
    async deleteGroupConfirm(row) {
      if (!confirm(
        `Delete the entire group "${row.name}" and all ${row.childCount} message${row.childCount === 1 ? '' : 's'}? This removes them from Apple and cannot be undone.`
      )) return;
      // The cascade deletes each member from Apple and can take a few seconds.
      this.deletingGroupName = row.name;
      try {
        await this.$store.dispatch('retention/deleteGroup', { groupId: row.groupId });
      } finally {
        this.deletingGroupName = null;
      }
    },
    selectNone() {
      this.selectedIds = new Set();
      this.lastClickedIndex = null;
    },
    async bulkSubmit() {
      const items = this.submittableSelected.map((m) => m.id);
      if (items.length === 0) return;
      await this.runBulk(items, 'submit');
    },
    async bulkDelete() {
      const items = this.deletableSelected.map((m) => m.id);
      if (items.length === 0) return;
      if (!confirm(`Delete ${items.length} message${items.length === 1 ? '' : 's'}? This removes them from Apple and cannot be undone.`)) {
        return;
      }
      await this.runBulk(items, 'delete');
    },
    async runBulk(items, action) {
      this.bulkAction = action;
      const worker = async (id) => {
        if (action === 'submit') {
          await this.$store.dispatch('retention/submitMessage', { id, silent: true });
        } else {
          await this.$store.dispatch('retention/deleteMessage', { id, silent: true });
        }
      };
      await this.executeBulk({ items, worker });

      const ok = this.bulkResult.successes.length;
      const failed = this.bulkResult.failures.length;
      const verb = action === 'submit' ? 'submitted' : 'deleted';
      if (failed === 0) {
        notify(`Bulk ${verb}: ${ok} of ${items.length} succeeded.`, 'success');
        this.selectNone();
      } else {
        notify(`Bulk ${verb}: ${ok} succeeded, ${failed} failed.`, 'error');
        const next = new Set(this.selectedIds);
        for (const id of this.bulkResult.successes) next.delete(id);
        this.selectedIds = next;
      }
    },
    async retryFailed() {
      const items = this.retryableFailures.map((f) => f.item);
      if (items.length === 0) return;
      this.showResultModal = false;
      await this.runBulk(items, this.bulkAction);
    },
    closeResultModal() {
      this.closeBulkResultModal();
    }
  }
};
</script>

<style scoped>
.retention-messages {
  flex: 1;
  min-width: 0;
  padding: 0.5rem 0.25rem 6rem;
  color: rgba(255, 255, 255, 0.88);
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  margin-bottom: 1rem;
}

.page-header h2 {
  margin: 0 0 0.3rem;
  font-size: 1.5rem;
  color: #fff;
  font-weight: 600;
}

.hint {
  color: rgba(255, 255, 255, 0.55);
  font-size: 0.85rem;
  margin: 0;
  max-width: 70ch;
  line-height: 1.4;
}

.permission-note {
  color: rgba(255, 255, 255, 0.5);
  font-size: 0.85rem;
}

.empty {
  padding: 2rem;
  text-align: center;
  color: rgba(255, 255, 255, 0.55);
  background: rgba(255, 255, 255, 0.03);
  border: 1px dashed rgba(255, 255, 255, 0.12);
  border-radius: 8px;
}

.table-wrap {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 8px;
}

.messages-table {
  width: 100%;
  border-collapse: collapse;
  color: rgba(255, 255, 255, 0.85);
}

.messages-table th,
.messages-table td {
  text-align: left;
  padding: 0.7rem 0.85rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  font-size: 0.88rem;
  vertical-align: middle;
}

.messages-table thead th {
  background: rgba(255, 255, 255, 0.04);
  font-weight: 600;
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.messages-table tr.data-row {
  cursor: pointer;
  transition: background-color 0.12s ease;
}

.messages-table tr.data-row:hover {
  background: rgba(3, 179, 218, 0.06);
}

.messages-table tr.data-row.selected { background: rgba(3, 179, 218, 0.12); }
.messages-table tr.data-row.selected:hover { background: rgba(3, 179, 218, 0.18); }

.messages-table tr:last-child td {
  border-bottom: none;
}

.col-check { width: 36px; text-align: center; }
.col-check input[type="checkbox"] {
  cursor: pointer;
  accent-color: #03B3DA;
}

.col-status   { width: 110px; }
.col-name     { width: 16%; }
.col-language { width: 130px; }
.col-id       { width: 96px; }
.col-type     { width: 90px; }
.col-title    { width: 16%; }
.col-body     { width: auto; }
.col-req      { width: 90px; text-align: right; }

.empty-row {
  text-align: center !important;
  padding: 1.6rem !important;
  color: rgba(255, 255, 255, 0.45);
  font-style: italic;
}

.filter-toolbar {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
  flex-wrap: wrap;
}

.search-input {
  position: relative;
  flex: 1;
  min-width: 220px;
  max-width: 360px;
}

.search-input i.fa-search {
  position: absolute;
  left: 0.65rem;
  top: 50%;
  transform: translateY(-50%);
  color: rgba(255, 255, 255, 0.4);
  font-size: 0.78rem;
  pointer-events: none;
}

.search-input input {
  width: 100%;
  padding: 0.45rem 2rem 0.45rem 2rem;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.04);
  color: rgba(255, 255, 255, 0.92);
  font-size: 0.85rem;
}

.search-input input:focus {
  outline: none;
  border-color: #03B3DA;
  background: rgba(255, 255, 255, 0.06);
  box-shadow: 0 0 0 2px rgba(3, 179, 218, 0.15);
}

.search-input .clear-icon {
  position: absolute;
  right: 0.7rem;
  top: 50%;
  transform: translateY(-50%);
  color: rgba(255, 255, 255, 0.4);
  font-size: 0.78rem;
  cursor: pointer;
}

.search-input .clear-icon:hover {
  color: #fff;
}

.filter-label {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.78rem;
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.filter-select {
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 6px;
  color: rgba(255, 255, 255, 0.92);
  padding: 0.4rem 0.6rem;
  font-size: 0.85rem;
  text-transform: none;
  letter-spacing: normal;
}

.filter-select:focus {
  outline: none;
  border-color: #03B3DA;
}

.filter-select option {
  background: #1a2332;
  color: #fff;
}

.filter-clear {
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.18);
  color: rgba(255, 255, 255, 0.7);
  border-radius: 6px;
  padding: 0.35rem 0.8rem;
  font-size: 0.78rem;
  cursor: pointer;
}

.filter-clear:hover {
  background: rgba(255, 255, 255, 0.06);
  color: #fff;
}

.filter-count {
  margin-left: auto;
  color: rgba(255, 255, 255, 0.45);
  font-size: 0.78rem;
  font-variant-numeric: tabular-nums;
}

.language-cell {
  font-size: 0.82rem;
}

.language-chip {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 999px;
  background: rgba(3, 179, 218, 0.12);
  color: #6dd0ea;
  font-size: 0.74rem;
  font-weight: 500;
}

.muted {
  color: rgba(255, 255, 255, 0.3);
}

/* --- Message groups --- */
.group-header-row {
  cursor: pointer;
  background: rgba(3, 179, 218, 0.05);
  transition: background-color 0.12s ease;
}
.group-header-row:hover {
  background: rgba(3, 179, 218, 0.1);
}
.group-label-cell {
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
}
.group-caret {
  display: inline-block;
  width: 14px;
  margin-right: 0.5rem;
  color: rgba(255, 255, 255, 0.55);
  font-size: 0.78rem;
}
.group-icon {
  margin-right: 0.45rem;
  color: #6dd0ea;
  font-size: 0.82rem;
}
.group-name {
  font-size: 0.9rem;
}
.group-badge {
  display: inline-block;
  margin-left: 0.6rem;
  padding: 1px 8px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.7rem;
  font-weight: 500;
}
/* Group header: total Req # across all members. */
.group-req-total {
  font-weight: 600;
  color: rgba(255, 255, 255, 0.85);
}

/* Blocking overlay shown while a whole group is being deleted. */
.group-delete-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(4px);
}
.group-delete-card {
  display: flex;
  align-items: center;
  gap: 1.1rem;
  background: #1a2332;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  padding: 1.4rem 1.8rem;
  min-width: 360px;
  max-width: 90vw;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.4);
  color: rgba(255, 255, 255, 0.88);
}
.group-delete-card h3 {
  margin: 0 0 0.25rem;
  font-size: 1rem;
  color: #fff;
  font-weight: 600;
}
.group-delete-card p {
  margin: 0;
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.6);
}
/* Member rows sit visually nested under their group header. */
.messages-table tr.data-row.group-child .name-cell {
  padding-left: 1.9rem;
}
.messages-table tr.data-row.group-child td:first-child {
  box-shadow: inset 3px 0 0 rgba(3, 179, 218, 0.35);
}

.name-cell {
  color: rgba(255, 255, 255, 0.95);
  font-weight: 600;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 0.83rem;
}
.col-actions  {
  width: 44px;
  text-align: right;
  position: relative;
}

/* round corners on the actual cells so removing wrap overflow:hidden doesn't leave square edges */
.messages-table thead tr th:first-child { border-top-left-radius: 8px; }
.messages-table thead tr th:last-child  { border-top-right-radius: 8px; }
.messages-table tbody tr:last-child td:first-child { border-bottom-left-radius: 8px; }
.messages-table tbody tr:last-child td:last-child  { border-bottom-right-radius: 8px; }

.state-pill {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 0.68rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.state-draft     { background: rgba(255, 255, 255, 0.1);  color: #c8d0d9; }
.state-pending   { background: rgba(255, 192, 50, 0.15);  color: #ffc832; }
.state-approved  { background: rgba(46, 204, 113, 0.18);  color: #2ecc71; }
.state-rejected  { background: rgba(231, 76, 60, 0.18);   color: #e74c3c; }

.id-cell {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 0.78rem;
  cursor: copy;
  color: rgba(255, 255, 255, 0.7);
  background: none;
}

.id-cell:hover {
  color: #03B3DA;
}

.type-cell {
  white-space: nowrap;
}

.type-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 600;
  border: 1px solid transparent;
  white-space: nowrap;
}

.type-chip i {
  font-size: 0.7rem;
}

.type-chip-grey   { background: rgba(255, 255, 255, 0.06); color: rgba(255, 255, 255, 0.78); border-color: rgba(255, 255, 255, 0.12); }
.type-chip-blue   { background: rgba(52, 152, 219, 0.18); color: #6dd0ea; border-color: rgba(52, 152, 219, 0.4); }
.type-chip-indigo { background: rgba(155, 89, 182, 0.18); color: #d59ded; border-color: rgba(155, 89, 182, 0.4); }
.type-chip-green  { background: rgba(46, 204, 113, 0.18); color: #58d68d; border-color: rgba(46, 204, 113, 0.4); }
.type-chip-orange { background: rgba(255, 192, 50, 0.18); color: #ffc832; border-color: rgba(255, 192, 50, 0.4); }

.title-cell {
  color: rgba(255, 255, 255, 0.78);
  font-weight: 500;
}

.body-cell {
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.84rem;
  line-height: 1.4;
}

.req-cell {
  text-align: right;
  font-variant-numeric: tabular-nums;
  color: rgba(255, 255, 255, 0.75);
}

.col-actions {
  color: rgba(255, 255, 255, 0.55);
}

.danger {
  color: #e74c3c;
}

/* Popup menu positioning — anchor to the actions cell instead of body */
.col-actions ::v-deep .popup-container {
  position: relative;
  display: inline-block;
}

.col-actions ::v-deep .popup-container .trigger {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 4px;
  color: rgba(255, 255, 255, 0.55);
  cursor: pointer;
}

.col-actions ::v-deep .popup-container .trigger:hover {
  background: rgba(255, 255, 255, 0.06);
  color: #fff;
}

.col-actions ::v-deep .popup-content {
  top: calc(100% + 4px) !important;
  right: 0 !important;
  left: auto !important;
  background-color: #1a2332 !important;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
  padding: 6px !important;
  min-width: 180px;
  z-index: 50;
}

.col-actions ::v-deep .popup-content > div {
  width: 100%;
}

.col-actions ::v-deep .popup-content a {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 6px 10px;
  border-radius: 4px;
  color: rgba(255, 255, 255, 0.88);
  text-decoration: none;
  font-size: 0.85rem;
  white-space: nowrap;
  cursor: pointer;
}

.col-actions ::v-deep .popup-content a:hover {
  background: rgba(255, 255, 255, 0.06);
}

.col-actions ::v-deep .popup-content a.danger {
  color: #e74c3c;
}

.col-actions ::v-deep .popup-content a.danger:hover {
  background: rgba(231, 76, 60, 0.12);
}

/* Sticky bulk action bar */
.bulk-bar {
  position: fixed;
  bottom: 0;
  left: 260px;
  right: 0;
  background: rgba(20, 25, 38, 0.96);
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 -4px 14px rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(6px);
  padding: 0.85rem 1rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  z-index: 200;
  flex-wrap: wrap;
}

.bulk-count {
  font-size: 0.92rem;
  color: rgba(255, 255, 255, 0.85);
}
.bulk-count strong { color: #03B3DA; }

.bulk-bar-right {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.bulk-hint {
  width: 100%;
  margin: 0.4rem 0 0;
  font-size: 0.8rem;
  color: #ffb84d;
}

.link-btn {
  background: none;
  border: none;
  color: #03B3DA;
  font-size: 0.82rem;
  cursor: pointer;
  padding: 0.2rem 0.45rem;
  border-radius: 4px;
}
.link-btn:hover { background: rgba(3, 179, 218, 0.1); }

.btn {
  padding: 0.45rem 1rem;
  border-radius: 6px;
  border: none;
  font-weight: 600;
  font-size: 0.88rem;
  cursor: pointer;
  transition: background-color 0.12s ease, color 0.12s ease, border-color 0.12s ease;
}
.btn-primary {
  background: #03B3DA;
  color: #fff;
}
.btn-primary:hover { background: #04a3c5; }
.btn-primary:disabled {
  background: rgba(3, 179, 218, 0.35);
  color: rgba(255, 255, 255, 0.6);
  cursor: not-allowed;
}
/* Custom danger button — avoids the global template's .btn-danger gradient. */
.btn-remove-default {
  background: rgba(231, 76, 60, 0.12);
  color: #ff6b5e;
  border: 1px solid rgba(231, 76, 60, 0.5);
}
.btn-remove-default:hover {
  background: #e74c3c;
  color: #fff;
  border-color: #e74c3c;
}
</style>
